// Timeline scrubber math (spec 17 §3.1/§4.3 — 24h timeline + event markers).
// REF: lsappv3 SDPlaybackView `sd_timeline` (startFraction/widthFraction 0~1 of day)
//      + Flutter hls_playback_page `hls_timebar` (progressBar + playhead + eventMarker).
// Pure + unit-tested so the fraction ↔ µs mapping and segment coalescing are verifiable
// without a DOM. Shared across all App shells (Home/Aura/Showcase/Ops) per 30 §2 (共用 core).

export interface TimeRange {
  /** local-day window start, µs epoch (inclusive). */
  fromUs: number;
  /** local-day window end, µs epoch (exclusive). */
  toUs: number;
}

export interface ClipSpan {
  clip_id: string;
  clip_start_ts: number;
  clip_end_ts: number;
  has_event?: boolean;
}

/** A coalesced recording block for the strip (adjacent clips merged, spec 17 §3.1). */
export interface Segment {
  startUs: number;
  endUs: number;
  /** 0~1 left offset within the window. */
  startFraction: number;
  /** 0~1 width within the window. */
  widthFraction: number;
}

/** Clamp µs to [from,to) and return its 0~1 position in the window. */
export function usToFraction(us: number, range: TimeRange): number {
  const span = range.toUs - range.fromUs;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (us - range.fromUs) / span));
}

/** Inverse of {@link usToFraction}: a 0~1 position → µs within the window. */
export function fractionToUs(fraction: number, range: TimeRange): number {
  const f = Math.min(1, Math.max(0, fraction));
  return Math.round(range.fromUs + f * (range.toUs - range.fromUs));
}

/**
 * Coalesce a clip list into display segments: clips closer than `gapUs` merge into one
 * block (so a continuous recording shows as one bar, not 5,760 hairlines). Input need not
 * be sorted. Clips outside the window are clipped to its edges.
 */
export function coalesceSegments(clips: ClipSpan[], range: TimeRange, gapUs = 2_000_000): Segment[] {
  const inWindow = clips
    .filter((c) => c.clip_end_ts > range.fromUs && c.clip_start_ts < range.toUs)
    .map((c) => ({ s: Math.max(c.clip_start_ts, range.fromUs), e: Math.min(c.clip_end_ts, range.toUs) }))
    .sort((a, b) => a.s - b.s);

  const segs: Segment[] = [];
  for (const c of inWindow) {
    const last = segs[segs.length - 1];
    if (last && c.s - last.endUs <= gapUs) {
      if (c.e > last.endUs) last.endUs = c.e;
    } else {
      segs.push({ startUs: c.s, endUs: c.e, startFraction: 0, widthFraction: 0 });
    }
  }
  for (const seg of segs) {
    seg.startFraction = usToFraction(seg.startUs, range);
    seg.widthFraction = Math.max(0, usToFraction(seg.endUs, range) - seg.startFraction);
  }
  return segs;
}

/** An event to annotate on the timeline (with its display type). */
export interface RulerEvent {
  tsUs: number;
  /** type emoji (eventsLib cluster icon); absent → generic. */
  icon?: string;
  /** localized type label, for the hover tooltip. */
  typeLabel?: string;
  /** record mode the event was emitted in (shown in the hover tooltip). */
  mode?: string;
}

/** A drawn annotation: one event, or several coalesced into a counted cluster. */
export interface EventCluster {
  /** 0~1 anchor position (earliest event in the cluster). */
  fraction: number;
  /** earliest event µs — the seek target when activated. */
  startTsUs: number;
  /** how many events fall under this marker (1 = single). */
  count: number;
  /** single-event icon (count===1); clusters render the count instead. */
  icon?: string;
  /** the events under this marker, earliest-first. */
  items: RulerEvent[];
}

/**
 * Coalesce events that sit closer than `minGapFraction` of the window into one counted
 * cluster (so a dense burst shows as a single "N" marker, not an unreadable pile). Greedy
 * over time order; clusters anchor at their earliest event. Events outside the window drop.
 */
export function clusterEvents(events: RulerEvent[], range: TimeRange, minGapFraction = 0.02): EventCluster[] {
  const span = range.toUs - range.fromUs;
  if (span <= 0) return [];
  const inWindow = events
    .filter((e) => e.tsUs >= range.fromUs && e.tsUs <= range.toUs)
    .sort((a, b) => a.tsUs - b.tsUs);

  const clusters: EventCluster[] = [];
  for (const e of inWindow) {
    const fraction = (e.tsUs - range.fromUs) / span;
    const last = clusters[clusters.length - 1];
    // Measure the gap from the cluster's ANCHOR (its first event), not the previous event,
    // so a long run of moderately-spaced events can't chain into one day-spanning blob — a
    // cluster's width is bounded to minGapFraction.
    if (last && fraction - last.fraction <= minGapFraction) {
      last.items.push(e);
      last.count += 1;
      last.icon = undefined; // multi → show count, not a single icon
    } else {
      clusters.push({ fraction, startTsUs: e.tsUs, count: 1, icon: e.icon, items: [e] });
    }
  }
  return clusters;
}

/** One graduation on the measuring-tape ruler. Minor ticks are short & unlabeled;
 *  major ticks are tall and carry a time label (UTC, matching the UTC+0 day model). */
export interface RulerTick {
  /** 0~1 position within the window. */
  fraction: number;
  /** true = tall labeled graduation; false = short fine tick. */
  major: boolean;
  /** time label, present on major ticks only. */
  label?: string;
}

/**
 * Nice (minor, major) step pairs in µs for the ruler. `major` is always an integer
 * multiple of `minor` AND aligned to an absolute time boundary, so majors land on
 * round clock times (e.g. whole minutes/hours) regardless of the window's left edge.
 */
const RULER_STEPS_US: { minor: number; major: number }[] = [
  { minor: 1_000_000, major: 10_000_000 }, // 1s / 10s
  { minor: 2_000_000, major: 20_000_000 }, // 2s / 20s
  { minor: 5_000_000, major: 60_000_000 }, // 5s / 1m
  { minor: 10_000_000, major: 60_000_000 }, // 10s / 1m
  { minor: 15_000_000, major: 60_000_000 }, // 15s / 1m
  { minor: 30_000_000, major: 300_000_000 }, // 30s / 5m
  { minor: 60_000_000, major: 300_000_000 }, // 1m / 5m
  { minor: 120_000_000, major: 600_000_000 }, // 2m / 10m
  { minor: 300_000_000, major: 1_800_000_000 }, // 5m / 30m
  { minor: 600_000_000, major: 3_600_000_000 }, // 10m / 1h
  { minor: 900_000_000, major: 3_600_000_000 }, // 15m / 1h
  { minor: 1_800_000_000, major: 7_200_000_000 }, // 30m / 2h
  { minor: 3_600_000_000, major: 21_600_000_000 }, // 1h / 6h
  { minor: 7_200_000_000, major: 43_200_000_000 }, // 2h / 12h
  { minor: 21_600_000_000, major: 86_400_000_000 }, // 6h / 24h
];

/** HH:MM (or HH:MM:SS for sub-minute majors), shifted by `offsetMs` (display timezone). */
function formatRulerLabel(us: number, majorUs: number, offsetMs: number): string {
  const d = new Date(us / 1000 + offsetMs);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  if (majorUs < 60_000_000) return `${hh}:${mm}:${String(d.getUTCSeconds()).padStart(2, '0')}`;
  return `${hh}:${mm}`;
}

/** A µs epoch → "YYYY-MM-DD HH:MM:SS" in the display timezone (UTC + offsetMin minutes). */
export function formatTimestampTz(us: number, offsetMin = 0): string {
  const d = new Date(us / 1000 + offsetMin * 60_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

/**
 * Measuring-tape ruler graduations across the window: dense minor ticks with periodic
 * tall, labeled major ticks. The step pair is the finest that keeps the minor-tick count
 * ≤ `maxMinorTicks` (so it stays dense but bounded). Major ticks land on round clock values
 * IN THE DISPLAY TIMEZONE (`offsetMs`), and labels are rendered there too.
 */
export function rulerTicks(range: TimeRange, maxMinorTicks = 120, offsetMs = 0): RulerTick[] {
  const span = range.toUs - range.fromUs;
  if (span <= 0) return [];
  const step = RULER_STEPS_US.find((s) => span / s.minor <= maxMinorTicks) ?? RULER_STEPS_US[RULER_STEPS_US.length - 1];
  const offsetUs = offsetMs * 1000;
  const ticks: RulerTick[] = [];
  const first = Math.ceil(range.fromUs / step.minor) * step.minor;
  for (let tUs = first; tUs <= range.toUs; tUs += step.minor) {
    const major = (tUs + offsetUs) % step.major === 0; // align majors to tz-round times
    ticks.push({
      fraction: (tUs - range.fromUs) / span,
      major,
      label: major ? formatRulerLabel(tUs, step.major, offsetMs) : undefined,
    });
  }
  return ticks;
}
